import React, { useEffect, useRef, useState } from "react";

import { NavigateFunction, useNavigate } from "react-router-dom";
import useDashboardStore from "store/dashboard-store";
import useScadaStore from "store/scada-store";
import { ScadaPolygon } from "../scadaTypes";
import { safeNavigate } from "../utils/scadaUtils";

type ScadaPolygonProps = {
  scadaId: string | undefined;
  polygons: ScadaPolygon[];
};

const ScadaPolygons: React.FC<ScadaPolygonProps> = ({ scadaId, polygons }) => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [dragging, setDragging] = useState<{
    polygonIndex: number | null;
    pointIndex: number | null;
    startCursor?: { x: number; y: number };
  }>({ polygonIndex: null, pointIndex: null });
  const [isPolygonDragging, setIsPolygonDragging] = useState(false)

  let navigate: NavigateFunction | null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigate = useNavigate();
  } catch (_) {
    navigate = null;
  }

  const svgRef = useRef<SVGSVGElement>(null);
  const updatePolygons = useScadaStore(state => state.updatePolygons);
  const isEditMode = useDashboardStore(state => state.isEditMode);

  useEffect(() => {
    const resize = () => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        setSize({
          width: rect.width,
          height: rect.height,
        });
      }
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [svgRef.current]);

  const percentToPixel = (point: { x: number; y: number; id: string }) => ({
    id: point.id,
    x: (point.x / 100) * size.width,
    y: (point.y / 100) * size.height,
  });

  const pixelToPercent = (x: number, y: number) => ({
    x: Math.round((x / size.width) * 10000) / 100,
    y: Math.round((y / size.height) * 10000) / 100,
  });

  const handleMouseDown =
    (polygonIndex: number, pointIndex: number) =>
      (e: React.MouseEvent<SVGCircleElement, MouseEvent>) => {
        e.stopPropagation();
        updatePolygons(scadaId!, polygons);
        setDragging({ polygonIndex, pointIndex });
      };

  const handleMouseDownPolygon =
    (polygonIndex: number) =>
      (e: React.MouseEvent<Element, MouseEvent>) => {
        e.stopPropagation();
        if (!svgRef.current) return;

        const pt = svgRef.current.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        const cursor = pt.matrixTransform(svgRef.current.getScreenCTM()?.inverse());

        updatePolygons(scadaId!, polygons);
        setDragging({
          polygonIndex,
          pointIndex: null,
          startCursor: { x: cursor.x, y: cursor.y },
        });
      };

  const handleSvgMouseMove: React.MouseEventHandler<SVGSVGElement> = (e) => {
    if (dragging.polygonIndex !== null) {
      setIsPolygonDragging(true);
      handleMouseMove(e.nativeEvent);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (dragging.polygonIndex === null || !svgRef.current) return;

    const pt = svgRef.current.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const cursor = pt.matrixTransform(svgRef.current.getScreenCTM()?.inverse());

    const updated = [...polygons];
    const poly = updated[dragging.polygonIndex];

    if (dragging.pointIndex !== null) {
      poly.points[dragging.pointIndex] = {
        id: poly.points[dragging.pointIndex].id,
        ...pixelToPercent(cursor.x, cursor.y),
      };
    } else if (dragging.startCursor) {
      const dx = cursor.x - dragging.startCursor.x;
      const dy = cursor.y - dragging.startCursor.y;

      poly.points = poly.points.map(p => {
        const pixel = percentToPixel(p);
        return {
          id: p.id,
          ...pixelToPercent(pixel.x + dx, pixel.y + dy),
        };
      });

      setDragging(d => d && { ...d, startCursor: { x: cursor.x, y: cursor.y } });
    }

    updatePolygons(scadaId!, updated);
  };

  const handleMouseUp = () => {
    setIsPolygonDragging(false)
    setDragging({ polygonIndex: null, pointIndex: null });
  };

  return (
    <svg
      ref={svgRef}
      width="100%"
      height="100%"
      className={`${isPolygonDragging ? "polygon-dragging-" : ""}scada-svg-polygons`}
      onMouseMove={handleSvgMouseMove}
      onMouseUp={handleMouseUp}
    >
      {size.width > 0 &&
        polygons.map(({ id, points, redirectTo, color, title }, polygonIndex) => {
          const pixelPoints = points.map(percentToPixel);
          const pointsAttr = pixelPoints.map(p => `${p.x},${p.y}`).join(" ");
          return (
            <g key={id}>
              <polygon
                points={pointsAttr}
                fill={color}
                onClick={
                  isEditMode ? undefined : () => safeNavigate(navigate, redirectTo)
                }
                className="scada-polygon"
                style={{ cursor: isEditMode ? "move" : "pointer" }}
                data-tooltip-id="scada-polygon-tooltip"
                data-tooltip-content={title?.trim()}
                onMouseDown={
                  isEditMode ? handleMouseDownPolygon(polygonIndex) : undefined
                }
              />
              {isEditMode &&
                pixelPoints.map((p, pointIndex) => (
                  <circle
                    key={p.id}
                    cx={p.x}
                    cy={p.y}
                    r={4}
                    fill="white"
                    stroke="black"
                    onMouseDown={handleMouseDown(polygonIndex, pointIndex)}
                    style={{ cursor: "move" }}
                  />
                ))}
            </g>
          );
        })}
    </svg>
  );
};

export default ScadaPolygons;
